/** Display order for palette sections in the left sidebar. */
import { getCustomActionPaletteEntries } from "./workflowSchemaUtils";
export const PALETTE_GROUP_ORDER = [
  "core",
  "flow_control",
  "whatsapp_messages",
  "whatsapp_notify",
  "whatsapp_await",
];

export const PALETTE_GROUP_LABELS = {
  core: "Core",
  flow_control: "Flow control",
  whatsapp_messages: "WhatsApp messages",
  whatsapp_notify: "Notify (one-way)",
  whatsapp_await: "Await user reply",
};

export const PALETTE_GROUP_COLORS = {
  core:
    "bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200",
  flow_control:
    "bg-[#EEEDFE] dark:bg-violet-950/40 border-[#AFA9EC] dark:border-violet-800 text-[#3C3489] dark:text-violet-300",
  whatsapp_messages:
    "bg-[#E1F5EE] dark:bg-emerald-950/40 border-[#9FE1CB] dark:border-emerald-800 text-[#085041] dark:text-emerald-300",
  whatsapp_notify:
    "bg-cyan-50 dark:bg-cyan-950/40 border-cyan-200 dark:border-cyan-800 text-cyan-900 dark:text-cyan-300",
  whatsapp_await:
    "bg-teal-50 dark:bg-teal-950/40 border-teal-200 dark:border-teal-800 text-teal-900 dark:text-teal-300",
  custom_actions:
    "bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800 text-violet-900 dark:text-violet-300"
};

/** Per-node colors inside the merged Core group (collect input, condition, action). */
export const PALETTE_CORE_ITEM_COLORS = {
  collect_input:
    "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-300",
  condition:
    "bg-[#FAEEDA] dark:bg-amber-950/40 border-[#FAC775] dark:border-amber-800 text-[#633806] dark:text-amber-300",
  action:
    "bg-[#FAECE7] dark:bg-orange-950/40 border-[#F5C4B3] dark:border-orange-800 text-[#712B13] dark:text-orange-300",
};

export function getPaletteItemColor(paletteType = "") {
  if (paletteType.startsWith("custom.")) return PALETTE_GROUP_COLORS.custom_actions;
  if (paletteType.includes("collect_input")) return PALETTE_CORE_ITEM_COLORS.collect_input;
  if (paletteType.includes("condition")) return PALETTE_CORE_ITEM_COLORS.condition;
  if (paletteType.includes("action")) return PALETTE_CORE_ITEM_COLORS.action;
  return null;
}

const CUSTOM_CATEGORY_LABELS = {
  ai: "Custom — AI",
  fun: "Custom — Fun",
  integration: "Custom — Integration",
  custom: "Custom actions"
};

const UNIFIED_TEMPLATE_LABELS = {
  ".notify": { label: "Notify — Template", description: "Send a WhatsApp message template to the customer." },
  ".await": { label: "Await Reply — Template", description: "Send a WhatsApp template and wait for customer reply." },
};
const UNIFIED_TEMPLATE_DEFAULT = { label: "Template", description: "Send a WhatsApp template message." };

function getCustomPaletteGroupKey(entry) {
  return `custom_${entry.category || "custom"}`;
}

function getCustomPaletteGroupTitle(key) {
  const category = key.replace(/^custom_/, "");
  return CUSTOM_CATEGORY_LABELS[category] || `Custom — ${category}`;
}

/**
 * Resolve sidebar group for a catalog palette entry.
 * @param {{ paletteType?: string, channel?: string }} node
 */
export function getPaletteGroupKey(node) {
  const paletteType = node?.paletteType || "";

  if (paletteType.startsWith("generic.")) {
    if (
      paletteType.includes("collect_input")
      || paletteType.includes("condition")
      || paletteType.includes("action")
    ) {
      return "core";
    }
    return "flow_control";
  }

  if (paletteType.startsWith("whatsapp.")) {
    if (paletteType.includes(".notify")) return "whatsapp_notify";
    if (paletteType.includes(".await")) return "whatsapp_await";
    return "whatsapp_messages";
  }

  const channel = (node?.channel || "").toLowerCase();
  if (channel === "whatsapp") return "whatsapp_messages";
  if (channel === "generic") return "flow_control";
  return "flow_control";
}

/**
 * Build ordered palette groups from GET_CATALOG `palette` and custom `actions`.
 */
export function buildPaletteGroups(catalogData) {
  const palette = catalogData?.palette ?? {};
  const rawNodes = Object.values(palette).flat();

  const processedNodes = [];
  const seenPaletteTypes = new Set();

  rawNodes.forEach((node) => {
    if (!node) return;

    if (typeof node.paletteType === "string") {
      if (node.paletteType.endsWith(".notify")) {
        const unifiedPaletteType = "whatsapp.notify";
        if (seenPaletteTypes.has(unifiedPaletteType)) {
          return;
        }
        seenPaletteTypes.add(unifiedPaletteType);

        processedNodes.push({
          ...node,
          paletteType: unifiedPaletteType,
          label: "Notify",
          description: "Send a message (text, image, video, document, or template) to the customer.",
          icon: "whatsapp-text-notify",
          nodeType: "NOTIFY_USER",
          defaultConfig: {
            recipientId: "",
            message: {
              type: "text",
              text: ""
            }
          }
        });
        return;
      }

      if (node.paletteType.endsWith(".await")) {
        const unifiedPaletteType = "whatsapp.await";
        if (seenPaletteTypes.has(unifiedPaletteType)) {
          return;
        }
        seenPaletteTypes.add(unifiedPaletteType);

        processedNodes.push({
          ...node,
          paletteType: unifiedPaletteType,
          label: "Await Reply",
          description: "Send a message and wait for customer reply.",
          icon: "whatsapp-text-await",
          nodeType: "AWAIT_USER_REPLY",
          defaultConfig: {
            recipientId: "",
            message: {
              type: "text",
              text: ""
            }
          }
        });
        return;
      }
    }

    if (
      typeof node.paletteType === "string" &&
      (node.paletteType.includes("whatsapp.template_text") ||
        node.paletteType.includes("whatsapp.template_media"))
    ) {
      const unifiedPaletteType = node.paletteType.replace(
        "whatsapp.template_media",
        "whatsapp.template_text"
      );

      if (seenPaletteTypes.has(unifiedPaletteType)) {
        return;
      }
      seenPaletteTypes.add(unifiedPaletteType);

      const unifiedNode = { ...node, paletteType: unifiedPaletteType };
      const suffixMatch = Object.entries(UNIFIED_TEMPLATE_LABELS)
        .find(([suffix]) => unifiedPaletteType.endsWith(suffix));
      const { label, description } = suffixMatch ? suffixMatch[1] : UNIFIED_TEMPLATE_DEFAULT;
      unifiedNode.label = label;
      unifiedNode.description = description;
      processedNodes.push(unifiedNode);
    } else {
      processedNodes.push(node);
    }
  });

  const grouped = new Map();
  processedNodes.forEach((node) => {
    const key = getPaletteGroupKey(node);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(node);
  });

  getCustomActionPaletteEntries(catalogData).forEach((entry) => {
    const key = getCustomPaletteGroupKey(entry);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(entry);
  });

  const customKeys = [...grouped.keys()]
    .filter((key) => key.startsWith("custom_"))
    .sort((a, b) => getCustomPaletteGroupTitle(a).localeCompare(getCustomPaletteGroupTitle(b)));

  const order = [
    ...PALETTE_GROUP_ORDER.filter((key) => grouped.has(key)),
    ...customKeys
  ];

  return order.map((key) => {
    const nodes = grouped.get(key);
    const sortedNodes =
      key === "core"
        ? [...nodes].sort((a, b) => {
            const rank = (paletteType = "") => {
              if (paletteType.includes("collect_input")) return 0;
              if (paletteType.includes("condition")) return 1;
              if (paletteType.includes("action")) return 2;
              return 3;
            };
            return rank(a.paletteType) - rank(b.paletteType);
          })
        : key.startsWith("custom_")
          ? [...nodes].sort((a, b) => (a.label || "").localeCompare(b.label || ""))
          : nodes;

    return {
      key,
      title: key.startsWith("custom_")
        ? getCustomPaletteGroupTitle(key)
        : PALETTE_GROUP_LABELS[key],
      color: key.startsWith("custom_")
        ? PALETTE_GROUP_COLORS.custom_actions
        : PALETTE_GROUP_COLORS[key],
      nodes: sortedNodes
    };
  });
}
